import { AccountType } from '@/core/enums/account-type';
import { ProfileAccount } from '@/profile/types/profile-account';
import { flow, uniqBy } from 'lodash';
import { ProfileFI } from '@/profile/types/profile-fi';

/**
 * Get the string url path for an account based on AccountType
 *
 * @param account - AccountType
 * @returns string url path
 */
export const getAccountLink = (account: ProfileAccount): string =>
	(window === window.parent
		? `/#/loan/${account.id}`
		: `/#/loan/${account.id}/plugin`);

/**
 * Sorts an array of accounts via originationDate
 *
 * @remarks
 *
 * If ascending is true then the account should be sorted from oldest to newest.
 * If ascending is false then the accounts should be sorted from newest to oldest.
 *
 * @param accounts - array of accounts
 *
 * @returns array of sorted accounts
 */
export const sortAccountsByDate = (accounts: ProfileAccount[], ascending = true): ProfileAccount[] => {
	let firstDateEpoch: number;
	let secondDateEpoch: number;
	let firstDate: string;
	let secondDate: string;

	return accounts.sort((first, second) => {
		firstDate = first.originationDate as string;
		secondDate = second.originationDate as string;
		firstDateEpoch = new Date(firstDate).getTime();
		secondDateEpoch = new Date(secondDate).getTime();

		return ascending
			? firstDateEpoch - secondDateEpoch
			: secondDateEpoch - firstDateEpoch;
	});
};

/**
 * Returns the account to select the default Institution by.
 *
 * @remarks
 *
 * If there is not an open account the returned account
 * will the first account in the array.
 * If there is at least one open account the returned account
 * will be the first account (sorted via openedDate) that is not closed.
 *
 * @param accounts - array of accounts
 *
 * @returns the default account or undefined if the determined
 * default account does not exist or cannot be found.
 */
export const getDefaultAccount = (accounts: ProfileAccount[]): ProfileAccount | null => {
	const hasOpenAccount = !!accounts.find((acc) => !acc.closedDate);

	if (!hasOpenAccount) {
		const [firstAccount] = sortAccountsByDate(accounts);
		return firstAccount;
	}

	const sortedAccounts = sortAccountsByDate(accounts);
	const firstOpenAccount = sortedAccounts.find((a) => !a.closedDate);

	return firstOpenAccount ?? null;
};

/**
 * Get the unique Financial Institutions of an array of accounts
 * If account type is defined then filter only the insitutions from accounts of that type
 *
 * @param accounts - array of accounts
 * @param type - account type
 *
 * @returns - array of Financial Institutions
 */
export const getInstitutionsFromAccounts = ({
	accounts,
	type,
}: {
	accounts: ProfileAccount[];
	type?: AccountType;
}): ProfileFI[] => {
	const institutions = flow(
		[
			type
				? (accs) => accs.filter((a: ProfileAccount) => a.kloansType === type)
				: (accs) => accs,
			(accs) => accs.map((a: ProfileAccount) => a.fi),
			(fis) => uniqBy(fis, 'id'),
		],
	)(accounts) as ProfileFI[];

	return institutions;
};

/**
 * Takes a loan and returns the last 4 digits of the account number
 * in a string format of '(*{last4})'
 *
 * @param account
 *
 * @returns formatted string
 */
export const maskAccountNumber = (account: ProfileAccount): string =>
	`(*${account.kloanNumber.substr(-4)})`;
